/**
 * -------------------------------------------
 * Host Config file.
 *
 * See:
 *   https://github.com/facebook/react/tree/master/packages/react-reconciler
 *   https://github.com/facebook/react/blob/master/packages/react-reconciler/src/forks/ReactFiberHostConfig.custom.js
 * -------------------------------------------
 */

import debugConstructor from 'debug'

import { createInstance } from '../utils/element'
import { CHILDREN, applyDisplayObjectProps } from '../utils/props'
import invariant from '../vendor/invariant'

const debug = debugConstructor('reconciler')

function recursivelyUncacheFiberNode(node) {
  for (let i = node.children.length - 1; i >= 0; i--) {
    const child = node.children[i]
    recursivelyUncacheFiberNode(child)
  }

  if (node._destroyed) {
    console.warn(
      'react-pixi: node is destroyed before unmount, possible tree mutation outside of react.',
    )
    return
  }

  node.destroy()
}

function appendChild(parent, child) {
  debug('appendChild')
  // console.log('appendChild', child.id)
  if (parent.addChild) {
    parent.addChild(child)

    if (typeof child.didMount === 'function') {
      child.didMount.call(child, child, parent)
    }
  }
}

function removeChild(parent, child) {
  debug('removeChild')
  recursivelyUncacheFiberNode(child)
  parent.removeChild(child)
}

function insertBefore(parent, child, beforeChild) {
  debug('insertBefore')
  invariant(child !== beforeChild, 'PixiFiber cannot insert node before itself')

  const childExists = parent.children.indexOf(child) !== -1
  if (childExists) {
    parent.removeChild(child)
  }

  const beforeChildIndex = parent.getChildIndex(beforeChild)
  parent.addChildAt(child, beforeChildIndex)
}

// get diff between 2 objects
// https://github.com/facebook/react/blob/97e2911/packages/react-dom/src/client/ReactDOMFiberComponent.js#L546
function diffProperties(pixiElement, type, lastProps, nextProps, rootContainerElement) {
  let updatePayload = []

  for (const propKey in lastProps) {
    if (
      Object.prototype.hasOwnProperty.call(nextProps, propKey) ||
      !Object.prototype.hasOwnProperty.call(lastProps, propKey) ||
      lastProps[propKey] == null
    ) {
      continue
    }
    if (propKey === CHILDREN) {
      // Noop. Text children not supported
      continue
    }
    // For all other deleted properties we add it to the queue. We use
    // the whitelist in the commit phase instead.
    updatePayload.push(propKey, null)
  }

  for (const propKey in nextProps) {
    const nextProp = nextProps[propKey]
    const lastProp = lastProps != null ? lastProps[propKey] : undefined

    if (
      !Object.prototype.hasOwnProperty.call(nextProps, propKey) ||
      nextProp === lastProp ||
      (nextProp == null && lastProp == null)
    ) {
      continue
    }
    if (propKey === CHILDREN) {
      // Noop. Text children not supported
      continue
    }
    // For any other property we always add it to the queue and then we
    // filter it out using the whitelist during the commit.
    updatePayload.push(propKey, nextProp)
  }

  // Graphics is not pure
  if (type === 'Graphics') {
    return updatePayload
  }

  return updatePayload.length ? updatePayload : null
}

export default {
  getRootHostContext(rootContainer) {
    debug('getRootHostContext', rootContainer)
    return null
  },

  getChildHostContext(parentHostContext) {
    return parentHostContext
  },

  getPublicInstance(instance) {
    return instance
  },

  prepareForCommit(containerInfo) {
    return null
  },

  preparePortalMount(portalInstance) {},

  resetAfterCommit(containerInfo) {
    containerInfo._reactPixiRoot.layouter(containerInfo)
    containerInfo._reactPixiRoot.renderer(containerInfo)
  },

  createInstance,

  appendInitialChild: appendChild,

  finalizeInitialChildren(element, type, props) {
    debug('finalizeInitialChildren', element, type, props)
    return false
  },

  // Return false to prevent update
  prepareUpdate(pixiElement, type, oldProps, newProps, rootContainerInstance, hostContext) {
    debug('prepareUpdate')
    return diffProperties(pixiElement, type, oldProps, newProps, rootContainerInstance)
  },

  shouldSetTextContent(type, props) {
    return false
  },

  shouldDeprioritizeSubtree(type, props) {
    const isAlphaVisible = typeof props.alpha === 'undefined' || props.alpha > 0
    const isRenderable = typeof props.renderable === 'undefined' || props.renderable === true
    const isVisible = typeof props.visible === 'undefined' || props.visible === true

    return !(isAlphaVisible && isRenderable && isVisible)
  },

  createTextInstance(text, rootContainerInstance, internalInstanceHandler) {
    invariant(false, 'PixiFiber does not support text instances. Use `<Text /> component` instead.')
  },

  scheduleTimeout: setTimeout,

  cancelTimeout: clearTimeout,

  noTimeout: -1,

  now: Date.now,

  isPrimaryRenderer: false,

  supportsMutation: true,
  supportsMicrotasks: true,

  scheduleMicrotask: queueMicrotask,
  /**
   * -------------------------------------------
   * Mutation
   * -------------------------------------------
   */

  appendChild,

  appendChildToContainer: appendChild,

  removeChild,

  removeChildFromContainer: removeChild,

  insertBefore,

  insertInContainerBefore: insertBefore,

  commitUpdate(instance, updatePayload, type, oldProps, newProps) {
    debug('commitUpdate', instance, updatePayload, type)
    let applyProps = instance && instance.applyProps
    if (typeof applyProps !== 'function') {
      applyProps = applyDisplayObjectProps
    }
    applyProps(instance, oldProps, newProps)
  },

  detachDeletedInstance() {
    // noop
  },

  commitMount(instance, updatePayload, type, oldProps, newProps) {
    // noop
  },

  commitTextUpdate(textInstance, oldText, newText) {
    // noop
  },

  resetTextContent(pixiElement) {
    // noop
  },

  clearContainer(parent) {
    const { children } = parent
    for (let i = children.length - 1; i >= 0; i--) {
      removeChild(parent, children[i])
    }
  },
}
